import { AssetReference } from '@domain/element-asset';
import { WIDGET_PROPERTY_PREFIX } from '@domain/widget';
import { PromiseResolver } from '@studio/utils/promises';
import { getWidgetContentUrlPropertyOfElement } from '@studio/utils/element.utils';
export const codePropertyRegex = /^(html|css|ts|js)$/;
export function stripCustomPropertyPrefix(propertyName) {
    return propertyName.replace(WIDGET_PROPERTY_PREFIX, '');
}
export function applyCustomPropertyPrefix(propertyName) {
    if (propertyName.indexOf(WIDGET_PROPERTY_PREFIX) > -1) {
        return propertyName;
    }
    return `${WIDGET_PROPERTY_PREFIX}${propertyName}`;
}
export function hasWidgetReference(property) {
    return property.name === AssetReference.Widget;
}
export function isCustomProperty(property) {
    return property.name.indexOf(WIDGET_PROPERTY_PREFIX) > -1;
}
export function isWidgetCodeProperty(property) {
    return property.name !== undefined && property.name.match(codePropertyRegex) !== null;
}
export function getIframeIdFromEvent(mouseEvent) {
    var _a, _b, _c, _d, _e;
    const currentTarget = mouseEvent.currentTarget;
    if (!currentTarget) {
        return;
    }
    if (currentTarget.defaultView) {
        return (_b = (_a = currentTarget.defaultView) === null || _a === void 0 ? void 0 : _a.frameElement) === null || _b === void 0 ? void 0 : _b.id;
    }
    return (_e = (_d = (_c = currentTarget === null || currentTarget === void 0 ? void 0 : currentTarget.ownerDocument) === null || _c === void 0 ? void 0 : _c.defaultView) === null || _d === void 0 ? void 0 : _d.frameElement) === null || _e === void 0 ? void 0 : _e.id;
}
export function translateOpenEventPosition(mouseEvent, widgetInstance) {
    if (!(widgetInstance === null || widgetInstance === void 0 ? void 0 : widgetInstance.instance)) {
        return {
            x: mouseEvent.clientX,
            y: mouseEvent.clientY
        };
    }
    const { x: widgetX, y: widgetY } = widgetInstance.instance;
    // mouseEvent.clientX and clientY values are relative to the iframe
    return {
        x: mouseEvent.clientX + widgetX,
        y: mouseEvent.clientY + widgetY
    };
}
export function isValidWidgetResourceData(data) {
    return !!data && typeof data === 'object' && 'html' in data && 'css' in data && 'js' in data;
}
const widgetResourceCache = new Map();
export async function fetchWidgetFromResource(resourceUrl) {
    const existingResource = widgetResourceCache.get(resourceUrl);
    if (existingResource) {
        await existingResource.promise;
        if (existingResource.code) {
            return existingResource.code;
        }
    }
    const loadWidgetResource = new PromiseResolver();
    const promiseObj = { promise: loadWidgetResource.promise };
    let response;
    widgetResourceCache.set(resourceUrl, promiseObj);
    try {
        response = await (await fetch(resourceUrl)).json();
    }
    catch (e) {
        loadWidgetResource.reject();
        throw new Error('Could not fetch widget resource.');
    }
    if (!isValidWidgetResourceData(response)) {
        throw new Error('Invalid widget resource data.');
    }
    widgetResourceCache.set(resourceUrl, Object.assign(Object.assign({}, promiseObj), { code: response }));
    loadWidgetResource.resolve();
    return response;
}
export async function updateWidgetElementCodeProperties(element) {
    var _a;
    const resourceUrl = (_a = getWidgetContentUrlPropertyOfElement(element)) === null || _a === void 0 ? void 0 : _a.value;
    if (!resourceUrl) {
        return;
    }
    const { html, js, css } = await fetchWidgetFromResource(resourceUrl);
    for (const property of element.properties) {
        if (property.name === 'html') {
            property.value = html;
        }
        if (property.name === 'css') {
            property.value = css;
        }
        if (property.name === 'js') {
            property.value = js;
        }
    }
}
